// Entry point of the app core: one WebSocket to the API, one store per watched machine, commands, steppers.
import type { ServerMessage, WelcomeMessage } from '@brewcore/app-contract';
import { Connection, type NetState, type SocketFactory } from './connection.js';
import { CommandSender, type CommandInput, type CommandOutcome } from './commands.js';
import { MachineStore } from './store.js';
import { Stepper } from './stepper.js';
import { ShotsApi, ShotsStore, type FetchLike } from './shots.js';
import { BeansApi, BeansStore } from './beans.js';
import { ShotHistoryStore } from './shot-history.js';
import { HistoryBuffer } from './history.js';
import { HeatupStore } from './heatup.js';
import { LiveShotTracker } from './live-shot.js';

export interface ClientOptions {
  url: string;
  factory: SocketFactory;
  appVersion: string;
  isOnline?: () => boolean;
  commandTimeoutMs?: number;
  /** HTTP for the history (default: same origin, the browser's fetch; tests pass base URL and cookie) */
  http?: { base?: string; fetch?: FetchLike; headers?: Record<string, string> };
}

export interface ClientView {
  net: NetState;
  /** this device's clock when net last changed (the cloud bar shows a gap only once it lasts, core/calm.ts) */
  netSince: number;
  /** lost: the API ended the connection because the session is gone (4401/4403); check the session, then start(). */
  auth: 'ok' | 'lost';
  user: WelcomeMessage['user'] | null;
  instance: string | null;
  error: string | null;
}

export class BrewcoreClient {
  private readonly conn: Connection;
  private readonly stores = new Map<string, MachineStore>();
  private readonly steppers = new Map<string, Stepper>();
  private readonly shotStores = new Map<string, ShotsStore>();
  private readonly beanStores = new Map<string, BeansStore>();
  private readonly historyStores = new Map<string, ShotHistoryStore>();
  private readonly histories = new Map<string, HistoryBuffer>();
  private readonly liveShots = new Map<string, LiveShotTracker>();
  private readonly heatups = new Map<string, HeatupStore>();
  private readonly shotsApi: ShotsApi;
  private readonly beansApi: BeansApi;
  readonly commands: CommandSender;
  private offsetMs = 0;
  private view: ClientView = {
    net: 'connecting',
    netSince: Date.now(),
    auth: 'ok',
    user: null,
    instance: null,
    error: null,
  };
  private readonly listeners = new Set<() => void>();
  /** messages received (tests) */
  readonly log: ServerMessage[] = [];
  keepLog = false;

  constructor(private readonly opts: ClientOptions) {
    this.shotsApi = new ShotsApi(opts.http?.fetch, opts.http?.base ?? '', opts.http?.headers ?? {});
    this.beansApi = new BeansApi(opts.http?.fetch, opts.http?.base ?? '', opts.http?.headers ?? {});
    this.commands = new CommandSender(
      { send: (m) => this.conn.send(m) },
      { nowMs: () => Date.now() + this.offsetMs },
      opts.commandTimeoutMs,
    );
    this.conn = new Connection(
      { url: opts.url, factory: opts.factory, ...(opts.isOnline ? { isOnline: opts.isOnline } : {}) },
      {
        onOpen: () => {
          this.conn.send({ t: 'hello', appVersion: this.opts.appVersion });
          for (const id of this.stores.keys()) this.conn.send({ t: 'sub', machine: id });
        },
        onMessage: (m) => this.dispatch(m),
        onClose: () => {
          this.commands.onDisconnected();
          for (const s of this.stores.values()) s.markDisconnected();
        },
        onNetState: (net) => this.set({ net, netSince: Date.now() }),
        onAuthLost: () => this.set({ auth: 'lost' }),
      },
    );
  }

  start(): this {
    this.set({ auth: 'ok' });
    this.conn.start();
    return this;
  }

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  getSnapshot = (): ClientView => this.view;

  private set(patch: Partial<ClientView>): void {
    this.view = { ...this.view, ...patch };
    for (const fn of this.listeners) fn();
  }

  /** Clock of the app corrected by the API's time (intentAt, AD-12). */
  nowMs(): number {
    return Date.now() + this.offsetMs;
  }

  watch(machineId: string): MachineStore {
    let s = this.stores.get(machineId);
    if (!s) {
      s = new MachineStore(machineId);
      this.stores.set(machineId, s);
      if (this.conn.isOpen) this.conn.send({ t: 'sub', machine: machineId });
    }
    return s;
  }

  store(machineId: string): MachineStore | undefined {
    return this.stores.get(machineId);
  }

  /** The machine's learned heat-up (core/heatup.ts): the lead of the weekly plan's ready times. */
  heatup(machineId: string): HeatupStore {
    let h = this.heatups.get(machineId);
    if (!h) {
      h = new HeatupStore(machineId, {
        fetchImpl: this.opts.http?.fetch ?? ((input, init) => fetch(input, init)),
        base: this.opts.http?.base ?? '',
        headers: this.opts.http?.headers ?? {},
      });
      this.heatups.set(machineId, h);
    }
    return h;
  }

  /** History of a machine (shots, cloud-owned data); refreshed after every snapshot. */
  shots(machineId: string): ShotsStore {
    let s = this.shotStores.get(machineId);
    if (!s) {
      s = new ShotsStore(machineId, this.shotsApi);
      this.shotStores.set(machineId, s);
      this.watch(machineId);
      if (this.stores.get(machineId)?.getSnapshot().ready) void s.refresh();
    }
    return s;
  }

  /**
   * The history with filters (page "Verlauf"): its own list and figures; changes of a shot come from the main history
   * store, a new shot reloads it.
   */
  shotHistory(machineId: string): ShotHistoryStore {
    let s = this.historyStores.get(machineId);
    if (!s) {
      const store = new ShotHistoryStore(machineId, this.shotsApi);
      this.shots(machineId).onChange((id, rev, change) => store.apply(id, rev, change));
      this.historyStores.set(machineId, store);
      s = store;
    }
    return s;
  }

  /** The bean library of a machine (cloud-owned); refreshed after every snapshot and on bean deltas. */
  beans(machineId: string): BeansStore {
    let s = this.beanStores.get(machineId);
    if (!s) {
      s = new BeansStore(machineId, this.beansApi);
      // values still collected by −/+ go to the bean they were set for, before another one is chosen (D-B02)
      s.beforeSelect = () => this.stepper(machineId).flush();
      this.beanStores.set(machineId, s);
      this.watch(machineId);
      if (this.stores.get(machineId)?.getSnapshot().ready) void s.refresh();
    }
    return s;
  }

  /** The running shot (live values during state 20). */
  liveShot(machineId: string): LiveShotTracker {
    let l = this.liveShots.get(machineId);
    if (!l) {
      l = new LiveShotTracker();
      this.liveShots.set(machineId, l);
    }
    return l;
  }

  /** Temperature history of the last 20 minutes. */
  history(machineId: string): HistoryBuffer {
    let h = this.histories.get(machineId);
    if (!h) {
      h = new HistoryBuffer();
      this.histories.set(machineId, h);
    }
    return h;
  }

  send(machineId: string, input: CommandInput, id?: string): Promise<CommandOutcome> {
    return this.commands.send(this.watch(machineId), input, id);
  }

  stepper(machineId: string): Stepper {
    let st = this.steppers.get(machineId);
    if (!st) {
      st = new Stepper(this.watch(machineId), (params) => this.send(machineId, { kind: 'set', params }));
      this.steppers.set(machineId, st);
    }
    return st;
  }

  /** Network break (tests) / back to foreground. */
  drop(): void {
    this.conn.drop();
  }

  reconnectNow(): void {
    this.conn.reconnectNow();
  }

  close(): void {
    for (const st of this.steppers.values()) st.dispose();
    this.conn.close();
  }

  private dispatch(m: ServerMessage): void {
    if (this.keepLog) this.log.push(m);
    switch (m.t) {
      case 'welcome':
        this.offsetMs = m.serverTime - Date.now();
        this.set({ user: m.user, instance: m.instance, error: null });
        return;
      case 'snapshot':
        this.stores.get(m.machine)?.applySnapshot(m);
        void this.shotStores.get(m.machine)?.refresh();
        void this.beanStores.get(m.machine)?.refresh();
        if (this.historyStores.get(m.machine)?.getSnapshot().loaded)
          void this.historyStores.get(m.machine)?.refresh();
        return;
      case 'state':
        this.stores.get(m.machine)?.applyState(m);
        // which bean is current and which are on the machine come from state/beans (no bean row changes then)
        if (m.group === 'beans') this.beanStores.get(m.machine)?.onMachineBeans();
        return;
      case 'profile':
        this.stores.get(m.machine)?.applyProfile(m);
        return;
      case 'live':
        this.stores.get(m.machine)?.applyLive(m);
        this.history(m.machine).applyLive(m);
        this.liveShot(m.machine).apply(m);
        return;
      case 'conn':
        this.stores.get(m.machine)?.applyConn(m);
        return;
      case 'flags':
        this.stores.get(m.machine)?.applyFlags(m);
        return;
      case 'ack':
        this.commands.onAck(m);
        return;
      case 'shot':
        this.shotStores.get(m.machine)?.onShot(m);
        this.historyStores.get(m.machine)?.onShot();
        return;
      case 'shotUpdate':
        this.shotStores.get(m.machine)?.onUpdate(m);
        return;
      case 'reference':
        this.shotStores.get(m.machine)?.onReference(m);
        return;
      case 'beanUpdate':
        this.beanStores.get(m.machine)?.onUpdate(m);
        return;
      case 'history':
        this.history(m.machine).applyHistory(m);
        return;
      case 'revoked':
        this.stores.get(m.machine)?.markRevoked();
        this.set({ error: 'revoked' });
        return;
      case 'error':
        this.set({ error: m.code });
        return;
      default:
        return;
    }
  }
}
